import React, { useState, useEffect } from 'react';
import { graphql, useStaticQuery, Link } from 'gatsby';
import Loadable from '@loadable/component';
import parse from 'html-react-parser';
import classNames from 'classnames';
import { NavMenuButton } from './index';
import { WpMenu } from '../index';
import { getPublishedIssues } from '../../../model/issue';
import LogoIcon from '../../../assets/svgs/logo-icon.svg';
import Logo from '../../../assets/svgs/logo.svg';
import LogoWords from '../../../assets/svgs/logo-words.svg';
import LinksBar from './links-bar';

const CollapseHandler = Loadable(() => import('./collapse-handler'));

const Header = ({ location, issueName: locationIssueName = '', onPlayVideo, postData, wpPage }) => {
  const {
    headerNav: { menuItems: headerNavMenuItems },
    issueNav: { menuItems: issueNavMenuItems },
    wp: {
      siteOptionsMetaboxSettings: {
        optionsIssuesAmount: issuesAmount,
        optionsIssuesAmountNumber: issuesAmountToDisplay,
        optionsIssuesHomepage: issueIds,
        optionsNavSettingsTitle: navInfoTitle,
        optionsNavSettingsText: navInfoText,
        optionsNavSettingsPage: navInfoPageUrl,
      },
    },
    allWpTghpTaxonomyIssue: { nodes: allIssues },
  } = useStaticQuery(graphql`
    query {
      headerNav: wpMenu(locations: { eq: HEADER_NAV }) {
        ...MenuItems
      }

      issueNav: wpMenu(locations: { eq: ISSUES_NAV }) {
        ...MenuItems
      }

      wp {
        ...SiteOptions
      }

      allWpTghpTaxonomyIssue {
        nodes {
          ...AllIssues
        }
      }

      allWpPage {
        ...AllWpPages
      }
    }
  `);

  /**
   *  Get published issues
   */
  let publishedIssues = getPublishedIssues(issueIds, allIssues);
  if (issuesAmount === 'amount' && Number(issuesAmountToDisplay) !== 0) {
    publishedIssues = getPublishedIssues(issueIds, allIssues).slice(0, issuesAmountToDisplay);
  }

  /**
   *  Filter published issues by location
   */
  let filteredPublishedIssues;
  switch (location) {
    case 'issues-listing':
      filteredPublishedIssues = publishedIssues.filter(
        (issue) => issue?.tghpwipIssuesTaxonomyCategory !== 'film',
      );
      break;
    case 'documentaries-listing':
      filteredPublishedIssues = publishedIssues.filter(
        (issue) => issue?.tghpwipIssuesTaxonomyCategory === 'film',
      );
      break;
    default:
      filteredPublishedIssues = publishedIssues;
  }

  const [issueName, setIssueName] = useState(filteredPublishedIssues[0]?.name);
  const [issueDate, setIssueDate] = useState(
    filteredPublishedIssues[0]?.tghpwipIssuesTaxonomyPublishDate,
  );
  const [issueType, setIssueType] = useState(
    filteredPublishedIssues[0]?.tghpwipIssuesTaxonomyCategory,
  );

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const changeIssueOnScroll = () => {
      const headerHeight = document.querySelector('.site-header').offsetHeight;
      const topOffset = window.pageYOffset + headerHeight + 50;
      const listedIssues = document.querySelectorAll('.issue-section');

      listedIssues.forEach((issue, idx) => {
        let sectionTopOffset = issue.offsetTop;
        if (topOffset >= sectionTopOffset) {
          setIssueName(filteredPublishedIssues[idx]?.name);
          setIssueDate(filteredPublishedIssues[idx]?.tghpwipIssuesTaxonomyPublishDate);
        }
      });
    };

    window.addEventListener('scroll', changeIssueOnScroll, { passive: true });

    return () => window.removeEventListener('scroll', changeIssueOnScroll);
  }, [filteredPublishedIssues]);

  const [showIssueNavList, setShowIssueNavList] = useState(false);
  const toggleIssueNavList = () => setShowIssueNavList(!showIssueNavList);

  useEffect(() => {
    if (locationIssueName) {
      for (const issue of filteredPublishedIssues) {
        if (issue.name === locationIssueName) {
          setIssueName(issue.name);
          setIssueDate(issue.tghpwipIssuesTaxonomyPublishDate);
          setIssueType(issue.tghpwipIssuesTaxonomyCategory);
        }
      }
    }
  }, [location, locationIssueName, filteredPublishedIssues]);

  const [isCollapsed, setIsCollapsed] = useState(false);

  /**
   *  Links bar component props
   */
  const LinksBarProps = {
    toggleIssueNavList: toggleIssueNavList,
    issueName: issueName,
    issueDate: issueDate,
    issueType: issueType,
    showIssueNavList: showIssueNavList,
    publishedIssues: filteredPublishedIssues,
    issueNavMenuItems: issueNavMenuItems,
    location: location,
    wpPage: wpPage,
  };

  const { tghpwipArticleVideo: videoCode, tghpwipArticleAudio: audioUrl } = postData || {};

  const LinksBarArticleMobileProps = {
    videoCode: videoCode,
    onPlayVideo: onPlayVideo,
    audioUrl: audioUrl,
  };

  return (
    <header className={classNames(['site-header', isCollapsed && 'site-header--collapsed'])}>
      <CollapseHandler isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} />
      <div className="site-header__masthead">
        <Link to="/">{location === 'single-article--documentary' ? <LogoWords /> : <Logo />}</Link>
        <NavMenuButton />
      </div>

      <div
        className={classNames([
          'site-header__links',
          location === 'single-article--article' &&
            (videoCode || audioUrl) &&
            'site-header__links--has-mobile-links',
        ])}
      >
        <LinksBar
          location={location}
          videoCode={videoCode}
          audioUrl={audioUrl}
          LinksBarProps={LinksBarProps}
          LinksBarArticleMobileProps={LinksBarArticleMobileProps}
        />
      </div>

      <nav className="site-header__nav site-nav">
        {headerNavMenuItems?.nodes && (
          <WpMenu className="site-nav__menu" menuItems={headerNavMenuItems.nodes} />
        )}
        <section className="site-nav__about">
          <div className="site-nav__about-logo">
            <LogoIcon />
          </div>
          <div className="site-nav__about-title">{navInfoTitle}</div>
          <div className="site-nav__about-bio">{navInfoText}</div>
          {navInfoPageUrl && (
            <a href={navInfoPageUrl} className="site-nav__about-read-more">
              Read more {parse(`&#8594`)}
            </a>
          )}
        </section>
      </nav>
    </header>
  );
};

export default Header;
